import type { Guide } from "../types";

export const gettingStartedGuides: Guide[] = [
  {
    slug: "welcome-tour",
    category: "getting-started",
    route: "/getting-started/welcome-tour",
    title: "A tour of your dashboard",
    summary:
      "Get oriented — the Welcome dashboard, the sidebar, the Space selector, quick search, and where help lives.",
    intro:
      "When you sign in you land on the Welcome dashboard. This short tour points out the main areas so you always know where to go next.",
    readingTime: 3,
    status: "draft",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "The Welcome dashboard",
        blocks: [
          {
            type: "p",
            text: "The Welcome page is your home base. Under a greeting and your organization's name it shows, live:",
          },
          {
            type: "ul",
            items: [
              "Workspace health — a score with critical alerts, warnings and uptime. View health details opens Screens.",
              "Quick actions — Pair screen, Assign channel, Create playlist, Upload media and View all screens.",
              "Key numbers — Total screens, Online, Offline, Channels, Content health and License status — the real devices using your licence and how many remain, or “Unlimited screens” on the Business plan (virtual screens don't count).",
              "Screen status distribution, Live screen activity, Playlist readiness, Recent activity and Screen life cycle.",
            ],
          },
          {
            type: "p",
            text: "“Last updated” and the Live badge at the top right show the page refreshes by itself. If you can assign content to screens, Apply changes sits next to them — it is at the right end of every page’s title row, and a number on it counts screens with changes that aren’t on them yet — click it to review and apply them.",
          },
        ],
        screenshot: {
          src: "getting-started/welcome-tour/01.png",
          alt: "The Welcome dashboard",
          caption: "The Welcome dashboard — your starting point.",
          todo: "Full Welcome dashboard: greeting, organization name, Last updated · Live and Apply changes on the title line (no bell/help/settings icons since PR #230).",
        },
      },
      {
        title: "The sidebar",
        blocks: [
          {
            type: "p",
            text: "The left sidebar is your main navigation: Welcome, Screens, Screen Groups (when enabled for your account), Channels, Playlists, Media, Apps, Screen Fit and Wall Studio, then Emergency Center and Canvas, and Modules (industry feature suites such as Event Management).",
          },
          {
            type: "p",
            text: "At the bottom are the language selector, Quick search, Help & guides (this help center), Support, and your name — click it for Account Settings (My account if you aren't an admin) or Logout.",
          },
          {
            type: "note",
            tone: "info",
            text: "You only see the pages your role allows, so a colleague's sidebar may be shorter than yours.",
          },
        ],
        screenshot: {
          src: "getting-started/welcome-tour/02.png",
          alt: "The left sidebar navigation",
          todo: "Capture the sidebar alone, scrolled so every item from Welcome to Modules and the bottom (language, Quick search, Help & guides, Support, user) is visible.",
        },
      },
      {
        title: "Switch Space & search",
        blocks: [
          {
            type: "p",
            text: "The Space selector at the top of the sidebar sets which Space you are working in — screens and content belong to one Space. Admins and the Owner can also choose All spaces.",
          },
          {
            type: "p",
            text: "Press ⌘K (Ctrl+K on Windows) or click Quick search to jump to any page, screen, channel, playlist or media file.",
          },
        ],
        screenshot: {
          src: "getting-started/welcome-tour/03.png",
          alt: "The quick search palette",
          todo: "Quick search (⌘K) palette open.",
        },
      },
    ],
    related: [
      { label: "Your first screen & first content", to: "/getting-started/first-screen-first-content" },
      { label: "Who sees what: people & Spaces", to: "/account/who-sees-what" },
    ],
    keywords: ["dashboard", "welcome", "sidebar", "navigation", "search", "space", "workspace", "start", "quick actions"],
  },
  {
    slug: "first-screen-first-content",
    category: "getting-started",
    route: "/getting-started/first-screen-first-content",
    title: "Your first screen & first content",
    summary: "A 5-minute path: pair or launch a screen, upload one image or video, and put it on the screen.",
    intro:
      "This quick path gets something on a screen as fast as possible. You need either a display running the PulsePanels player, or just a browser for a virtual screen.",
    prerequisites: ["You are signed in with a role that can pair screens and manage content."],
    readingTime: 5,
    status: "draft",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Get a screen",
        blocks: [
          {
            type: "p",
            text: "Click Screens in the sidebar. If your Space has no screens yet, you see two choices:",
          },
          {
            type: "ul",
            items: [
              "Pair your screen now — the display shows a 6-character code; type it and click Continue.",
              "Launch a Virtual Screen — a new browser tab becomes a screen; pair it with its QR code or its code.",
            ],
          },
          {
            type: "steps-ref",
            label: "Pair a screen",
            to: "/screens/pair-a-screen",
          },
        ],
        screenshot: {
          src: "getting-started/first-screen-first-content/01.png",
          alt: "The empty Screens page with the two ways to add a screen",
          todo: "Screens page in an empty Space: 'Okay, let's get a screen up and running' with the Pair your screen now and Launch a Virtual Screen cards.",
        },
      },
      {
        title: "Upload an image or video",
        blocks: [
          {
            type: "p",
            text: "Click Media in the sidebar, then Upload, and choose an image or video from your computer. It appears in your media library when the upload finishes.",
          },
          {
            type: "steps-ref",
            label: "Upload media",
            to: "/content/upload-media",
          },
        ],
        screenshot: {
          src: "getting-started/first-screen-first-content/02.png",
          alt: "The Media page with the Upload button",
          todo: "Media page with the Upload button highlighted and one uploaded image in the library.",
        },
      },
      {
        title: "Put it on the screen",
        blocks: [
          {
            type: "ol",
            items: [
              "Go back to Screens and click the ⋯ button on your screen's row.",
              "Choose Set content.",
              "Click Media on the left, open the folder and select your file.",
              "Click Confirm.",
            ],
          },
        ],
        screenshot: {
          src: "getting-started/first-screen-first-content/03.png",
          alt: "The Set content dialog on the Media tab with a file selected",
          todo: "Set content dialog, Media tab, one image selected, Confirm enabled.",
        },
      },
      {
        title: "Check it",
        blocks: [
          {
            type: "p",
            text: "Within seconds the display shows your file, and the Assigned content column names it. Not near the screen? Open ⋯ → Preview to see what it shows.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Next, try a playlist (several items in a loop) or a channel (several zones on one screen).",
          },
        ],
      },
    ],
    related: [
      { label: "Pair a screen", to: "/screens/pair-a-screen" },
      { label: "Launch a virtual screen", to: "/screens/launch-a-virtual-screen" },
      { label: "Assign content to a screen", to: "/screens/assign-content" },
      { label: "Build a playlist", to: "/content/build-a-playlist" },
    ],
    keywords: ["quickstart", "first", "getting started", "setup", "first screen", "publish"],
  },
  {
    slug: "invite-your-team",
    category: "getting-started",
    route: "/getting-started/invite-your-team",
    title: "Set up your team",
    summary: "From a brand-new account to a working team: create your Spaces, invite people, and give each one the right access.",
    intro:
      "Follow one example, Acme Retail, from its first sign-in to a team of three: Sara (the Owner), Omar (an Admin who sees everything) and Lina (who looks after the mall shop only). Watch the 2-minute tour, or follow the steps.",
    readingTime: 5,
    status: "published",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "Watch the tour",
        blocks: [
          {
            type: "video",
            src: "/videos/set-up-your-team.webm",
            poster: "/videos/set-up-your-team.png",
            caption: "Sara creates two Spaces and invites Omar and Lina; Lina then sees only the mall shop (2 min).",
          },
        ],
      },
      {
        title: "Your account is ready",
        blocks: [
          {
            type: "p",
            text: "Your PulsePanels contact creates your account with you as the Owner. It starts with one Space, called “Default workspace”, and a 7-day trial — or your licence period if your licence is already in place.",
          },
          {
            type: "p",
            text: "Sign in at app.pulsepanels.com with the temporary password you received by email, then set your own in Account Settings → My Account → Change password.",
          },
          {
            type: "note",
            tone: "info",
            text: "A Space is a separate area of your account — for example one per branch or department. Every screen and piece of content belongs to one Space.",
          },
        ],
      },
      {
        title: "Create your Spaces",
        blocks: [
          { type: "p", text: "Plan one Space per place or team. Acme Retail needs two: Head office and Mall shop." },
          {
            type: "ol",
            items: [
              "Account Settings → Spaces → Edit space: rename “Default workspace” to Head office and click Save Changes.",
              "Create new space: name it Mall shop, choose its time zone, and click Create space.",
            ],
          },
        ],
        screenshot: {
          src: "getting-started/invite-your-team/02.png",
          alt: "The Create new space dialog with a Space name, a description and a time zone",
          caption: "Create a Space and set its time zone.",
        },
      },
      {
        title: "Invite someone who sees everything",
        blocks: [
          {
            type: "p",
            text: "Account Settings → Users → Invite User. Enter Omar's email, choose the role Admin, and click Create invitation. An Admin sees every Space, so there is no Space to choose.",
          },
        ],
        screenshot: {
          src: "getting-started/invite-your-team/03.png",
          alt: "The Invite user dialog with the email, the Role set to Admin and no Space to choose",
          caption: "Admins see every Space.",
        },
      },
      {
        title: "Invite someone for one Space",
        blocks: [
          {
            type: "p",
            text: "Invite User again for Lina: choose the role Content manager and the Space Mall shop, then click Create invitation. She will see only the mall shop.",
          },
        ],
        screenshot: {
          src: "account/users-and-invitations/02.png",
          alt: "The Invite user dialog with the role Content manager and the Space Mall shop",
          caption: "Every role but Admin is for the Space you choose.",
        },
      },
      {
        title: "They accept",
        blocks: [
          {
            type: "p",
            text: "Each person receives “You're invited to join Acme Retail on PulsePanels”, clicks Accept invitation, enters their name and a password, and is signed in straight away.",
          },
        ],
      },
      {
        title: "Check who sees what",
        blocks: [
          { type: "p", text: "Account Settings → Users now shows each person's role, with their Spaces underneath:" },
          {
            type: "table",
            head: ["Signs in", "Sees", "Can"],
            rows: [
              ["Sara — Owner", "Every Space", "Everything"],
              ["Omar — Admin", "Every Space", "Everything except changing the Owner"],
              ["Lina — Content manager", "Mall shop only", "Pair the mall's screens, choose what they play, manage content"],
            ],
          },
        ],
        screenshot: {
          src: "account/users-and-invitations/01.png",
          alt: "The Users tab showing Owner and Admin with All Spaces, and the team members with their Space",
          caption: "Role and Spaces for every person.",
        },
      },
    ],
    related: [
      { label: "Who sees what: people & Spaces", to: "/account/who-sees-what" },
      { label: "Examples for your organization", to: "/account/team-examples" },
      { label: "Roles & permissions", to: "/account/roles-and-permissions" },
      { label: "Users & invitations", to: "/account/users-and-invitations" },
    ],
    keywords: ["invite", "team", "users", "roles", "permissions", "spaces", "set up", "new account", "onboarding"],
  },
  {
    slug: "get-help-from-support",
    category: "getting-started",
    route: "/getting-started/get-help-from-support",
    title: "Get help from support",
    summary:
      "Chat with the support team from any page, open a ticket with a photo or log attached, and follow the replies until it's resolved.",
    intro:
      "Every conversation with support is a ticket you can follow. Start one from the chat bubble on any page or from the Support page, attach a photo or a log, and you'll see the team's replies in the same thread — live while you're signed in, and by email when you're not. On some accounts an AI assistant answers first, and hands you to a person whenever you ask. Anyone who can sign in can contact support, whatever their role.",
    readingTime: 5,
    status: "draft",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "Open support",
        blocks: [
          {
            type: "p",
            text: "There are two ways in, and both lead to the same tickets:",
          },
          {
            type: "ul",
            items: [
              "The chat bubble at the bottom right of every page. Click it to open the support panel without leaving what you're doing. It shows Start a conversation, your Previous conversations with their status, and a list of topics under How can we help? — click a topic to start a conversation about it.",
              "Support at the bottom of the sidebar. It opens the Support page, with your conversations on the left and the selected one on the right.",
            ],
          },
          {
            type: "p",
            text: "A number on the chat bubble counts replies you haven't read yet. Click × to close the panel; your conversations stay where they are.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Looking for how-to steps instead? Help & guides, just above Support in the sidebar, opens this help center in a new tab.",
          },
        ],
        screenshot: {
          src: "getting-started/get-help-from-support/01.png",
          alt: "The support panel opened from the chat bubble, with Start a conversation, a previous conversation and the topic list",
          caption: "The chat bubble opens support on any page.",
          todo: "Screens page with the chat bubble open on its home view: Start a conversation, Previous conversations (one ticket, Waiting for support) and the How can we help? topics.",
        },
      },
      {
        title: "Start a conversation",
        blocks: [
          {
            type: "ol",
            items: [
              "On the Support page click Start a conversation (New conversation once you have tickets), or click Start a conversation in the chat bubble.",
              "Choose a Topic: Screen offline, Content not playing, Upload problem, Publishing problem, Account access, Billing or subscription, Feature request or Other.",
              "Under Describe your issue, say what you see, on which screen, and since when.",
              "Click Send message.",
            ],
          },
          {
            type: "p",
            text: "The ticket is named after its topic — for example “Content not playing” — and appears at the top of your conversations with the status Waiting for support. Support also sees which page you were on and your browser, so you don't need to type those.",
          },
        ],
        screenshot: {
          src: "getting-started/get-help-from-support/02.png",
          alt: "The Support page's How can we help? form with the topic Content not playing and a description of the issue",
          caption: "Pick a topic and describe the issue.",
          todo: "Support page (/support) for a user with no tickets yet, after Start a conversation: Topic Content not playing, a short description, Send message.",
        },
      },
      {
        title: "If the AI assistant answers first",
        blocks: [
          {
            type: "p",
            text: "On some accounts, an AI assistant replies to a new conversation first, usually within seconds. You’ll see “You're chatting with our AI assistant.” above the message box, its replies are marked AI assistant, and the ticket’s status is Chatting with assistant. If your account doesn’t have it, your conversation goes straight to the support team.",
          },
          {
            type: "ul",
            items: [
              "It answers from this help center and from read-only facts about your own account — for example which screens are offline and when they were last seen — and usually ends with a link to the guide that covers your question. It replies in the language you write in.",
              "It can’t change anything for you: it can’t restart screens, change settings, publish content or change a licence, and it never promises refunds or credits. It tells you how to do things yourself, or hands you over to a person.",
            ],
          },
          {
            type: "p",
            text: "To reach a person at any time, click Talk to a person. The assistant also hands over by itself for billing and licence questions, sign-in or account-access problems, or when it isn’t sure of the answer. You’ll see “We've let the team know.”, the status changes to Waiting for support, and a member of the team replies in the same conversation. The team sees the whole conversation and a short summary, so you don’t need to repeat yourself.",
          },
          {
            type: "note",
            tone: "info",
            text: "If nobody has replied after a while, a message tells you that you don’t need to keep the window open — you’ll get an email as soon as someone replies, and the conversation stays where it is.",
          },
        ],
      },
      {
        title: "Attach a photo, video or log",
        blocks: [
          {
            type: "p",
            text: "A photo of the screen often explains the problem faster than words. You can attach a file in two places:",
          },
          {
            type: "ul",
            items: [
              "When you start a conversation from the chat bubble — click Attach a file (optional) before Send message. Choose Change file to pick another, or × to remove it.",
              "In any open conversation — click Attach on the Support page, or the paperclip next to the message box in the chat bubble. The file is added to the thread straight away.",
            ],
          },
          {
            type: "p",
            text: "You can attach PNG, JPG, WEBP, PDF, TXT, LOG or MP4 files. Videos (MP4) can be up to 50 MB; images and documents up to 15 MB.",
          },
        ],
        screenshot: {
          src: "getting-started/get-help-from-support/03.png",
          alt: "A new message in the chat bubble with a topic, a description and a photo attached",
          caption: "Attach a photo of the screen before you send.",
          todo: "Chat bubble, Start a conversation: Topic Content not playing, a one-line description, one PNG attached (file name and size shown), Send message.",
        },
      },
      {
        title: "Follow the conversation",
        blocks: [
          {
            type: "p",
            text: "Click a conversation to open it. Your messages are on the right; replies are on the left, marked Support (or AI assistant). Type in the message box and press Enter to send (Shift+Enter for a new line), or click Send.",
          },
          {
            type: "p",
            text: "Each ticket shows where it stands:",
          },
          {
            type: "table",
            head: ["Status", "What it means"],
            rows: [
              ["Chatting with assistant", "The AI assistant is answering. Click Talk to a person to bring in the team."],
              ["Waiting for support", "The team has your latest message and will reply."],
              ["Waiting for you", "Support replied and needs an answer or a check from you."],
              ["Open", "The team is working on it."],
              ["Resolved", "The problem is solved. You can still reopen it."],
              ["Closed", "The ticket is finished. Start a new conversation if you need more help."],
            ],
          },
          {
            type: "note",
            tone: "info",
            text: "Your tickets are private to you: colleagues in your organization don't see them, and you don't see theirs.",
          },
        ],
        screenshot: {
          src: "getting-started/get-help-from-support/04.png",
          alt: "The Support page with one conversation in the list and its thread: the first message, an attached photo and a follow-up message",
          caption: "Your conversations on the left, the selected thread on the right.",
          todo: "Support page with one ticket selected: Waiting for support, the first message, an attachment card and a follow-up line; Mark resolved at the top right.",
        },
      },
      {
        title: "Get notified about replies",
        blocks: [
          {
            type: "ul",
            items: [
              "While you're signed in, replies appear in the thread straight away, and the chat bubble shows how many you haven't read.",
              "The first time you send a message from the chat bubble, your browser asks whether PulsePanels may show notifications. Allow it to get a desktop notification when support replies while the panel is closed — click it to jump to the conversation.",
              "By email: you receive a confirmation with your ticket number when you open a ticket, and an email when support replies. Click View & reply in that email, then answer from the Support page.",
            ],
          },
        ],
      },
      {
        title: "Resolve or reopen",
        blocks: [
          {
            type: "p",
            text: "Fixed? Click Mark resolved on the Support page (Mark as resolved in the chat bubble). Support can mark a ticket resolved too.",
          },
          {
            type: "p",
            text: "If the problem comes back, open the resolved ticket and click Reopen — it goes back to Waiting for support. A ticket can be reopened for 14 days after it was resolved; after that, or once it's Closed, start a new conversation.",
          },
        ],
      },
      {
        title: "Shortcuts from other pages",
        blocks: [
          {
            type: "p",
            text: "A few pages open the support panel for you, already filled in:",
          },
          {
            type: "ul",
            items: [
              "Screens → Wall View → click a screen's card → Controls tab → Report an Issue starts a conversation about that screen (“Attached:” shows its name), so support can see which screen you mean.",
              "Apps → Request App starts a Feature request with a short template for the app you'd like.",
            ],
          },
        ],
      },
    ],
    related: [
      { label: "A tour of your dashboard", to: "/getting-started/welcome-tour" },
      { label: "Screen details: volume, brightness, location & more", to: "/screens/screen-details" },
      { label: "How apps work (Request App)", to: "/apps/add-an-app" },
    ],
    keywords: ["support", "help", "ticket", "chat", "live chat", "contact", "conversation", "attach", "report an issue", "problem", "notifications", "ai assistant", "assistant", "chatbot", "bot", "talk to a person", "human"],
  },
];
